import { TICK_RATE, tuning } from "@meleeroyale/sim";
import { damageTaken } from "../arena/damage-numbers.ts";
import { t } from "../i18n/index.ts";
import type { ClientView, GameEvent } from "../net/game-client.ts";
import { clockFromTicks, percent, secondsFromTicks } from "../utils/format.ts";
import { BodySilhouette } from "./BodySilhouette.tsx";

/** Under the reticle while a combo is on: a filled pip per strike started, a hollow one per press waiting. */
function ComboPips({ chain, queued }: { chain: number; queued: number }) {
  const pips: boolean[] = [];
  for (let i = 0; i < chain + queued && i < tuning.combo.maxChain; i += 1) pips.push(i < chain);
  return (
    <div className="combo-pips" aria-hidden="true">
      {pips.map((thrown, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: pips have no identity beyond their slot
        <span key={i} className={thrown ? "is-thrown" : undefined} />
      ))}
    </div>
  );
}

export function Hud({
  view,
  events,
  locked,
}: {
  view: ClientView;
  events: GameEvent[];
  locked: boolean;
}) {
  const me = view.fighters[view.myId];
  const alive = view.fighters.filter((f) => f.alive).length;
  const fightTick = view.tick - view.fightStartTick;
  const shrinkEvery = tuning.cage.shrinkEverySeconds * TICK_RATE;
  const untilShrink =
    view.phase === "fight"
      ? shrinkEvery - (((fightTick % shrinkEvery) + shrinkEvery) % shrinkEvery)
      : 0;
  const countdown =
    view.phase === "countdown" ? Math.ceil((view.fightStartTick - view.tick) / TICK_RATE) : 0;

  return (
    <div className="hud" aria-live="polite">
      <div className="hud-top">
        <div className="hud-panel">
          <div className="hud-sub">
            {view.phase === "countdown" ? t.match.countdown : t.match.clock}
          </div>
          <div className="hud-clock num">
            {view.phase === "countdown" ? countdown : clockFromTicks(view.clockTicks)}
          </div>
          {view.phase === "fight" ? (
            <div className={`hud-sub num${view.cage.warning ? " hud-warning" : ""}`}>
              {view.cage.warning
                ? t.match.warning
                : `${t.match.nextShrink} ${secondsFromTicks(untilShrink)}s`}
            </div>
          ) : null}
        </div>
        <div className="hud-panel hud-alive">
          <div className="hud-sub">{t.match.alive}</div>
          <strong className="num">
            {alive}/{view.fighters.length}
          </strong>
        </div>
      </div>

      {view.waiting ? (
        <div className="hud-countdown">
          <div className="hud-sub">{t.match.waiting}</div>
        </div>
      ) : view.phase === "countdown" ? (
        <div className="hud-countdown">
          <div className="hud-sub">{t.match.countdown}</div>
          <strong className="num">{countdown}</strong>
        </div>
      ) : (
        <div />
      )}

      <div className="hud-bottom">
        {me ? (
          <div className="hud-panel hud-body">
            <BodySilhouette hp={me.hp} label={t.results.health} />
            <div className="stack stack-2 hud-stamina">
              <div className="hud-sub">
                {me.southpaw ? t.match.stance.southpaw : t.match.stance.orthodox}
                {me.checking
                  ? ` · ${t.match.checking}`
                  : me.coverZone >= 0
                    ? ` · ${t.match.covering[me.coverZone] ?? ""}`
                    : ""}
              </div>
              <div className="hud-sub">{me.exhausted ? t.match.exhausted : t.match.stamina}</div>
              <div className={`meter${me.exhausted ? " meter-exhausted" : ""}`}>
                <span style={{ width: `${percent(me.stamina, tuning.stamina.max)}%` }} />
              </div>
              <div className="hud-tally">
                <span className="hud-sub">
                  {t.match.tally.dealt}
                  <strong className="num">{Math.round(me.damageDealt)}</strong>
                </span>
                <span className="hud-sub hud-tally-taken">
                  {t.match.tally.taken}
                  <strong className="num">{Math.round(damageTaken(me.hp))}</strong>
                </span>
              </div>
              {!me.alive ? <div className="hud-sub">{t.match.spectating}</div> : null}
            </div>
          </div>
        ) : null}
        <div className="hud-feed">
          {events.slice(-4).map((e) => (
            <div key={e.at}>{e.text}</div>
          ))}
        </div>
      </div>
      {locked && me?.alive ? <div className="reticle" aria-hidden="true" /> : null}
      {locked && me?.alive && me.combo.chain + me.combo.queued > 1 ? (
        <ComboPips chain={me.combo.chain} queued={me.combo.queued} />
      ) : null}
    </div>
  );
}
